const CLONE_COUNT = 3;
const AUTOPLAY_DELAY = 4000;
const TRANSITION_MS = 320;
const SWIPE_MIN_DISTANCE = 50;

function initSingleTestimonialCarousel(carousel) {
  if (carousel.dataset.testimonialCarouselInitialized === 'true') {
    return () => {};
  }

  const track = carousel.querySelector('.testimonial-carousel__track');
  const viewport = carousel.querySelector('.testimonial-carousel__viewport');
  const pipButtons = Array.from(carousel.querySelectorAll('.pips__button'));
  const prevButton = carousel.querySelector('[data-testimonial-carousel-prev]');
  const nextButton = carousel.querySelector('[data-testimonial-carousel-next]');
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const hasPips = pipButtons.length > 0;
  const hasArrows = Boolean(prevButton && nextButton);

  if (!track || (!hasPips && !hasArrows) || (hasPips && hasArrows)) {
    return () => {};
  }

  const controlMode = hasPips ? 'pips' : 'arrows';
  const realSlides = Array.from(track.querySelectorAll('.testimonial-carousel__slide'));

  if (realSlides.length < 2) {
    return () => {};
  }

  if (controlMode === 'pips' && pipButtons.length !== realSlides.length) {
    return () => {};
  }

  carousel.dataset.testimonialCarouselInitialized = 'true';

  let allSlides = realSlides;

  if (controlMode === 'pips') {
    const prependClones = realSlides.slice(-CLONE_COUNT).map((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('data-clone', 'true');
      return clone;
    });

    const appendClones = realSlides.slice(0, CLONE_COUNT).map((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('data-clone', 'true');
      return clone;
    });

    prependClones.forEach((clone) => {
      track.insertBefore(clone, track.firstChild);
    });

    appendClones.forEach((clone) => {
      track.appendChild(clone);
    });

    allSlides = Array.from(track.querySelectorAll('.testimonial-carousel__slide'));
  }

  const toTrackIndex = (index) => (controlMode === 'pips' ? CLONE_COUNT + index : index);

  let selectedIndex = Math.max(
    0,
    Math.min(Number.parseInt(carousel.dataset.initialIndex ?? '0', 10), realSlides.length - 1)
  );
  let currentTrackIndex = toTrackIndex(selectedIndex);
  let autoplayTimer = null;
  let snapTimer = null;
  let isAnimating = false;
  let touchStartX = 0;
  let touchEndX = 0;
  let isSwiping = false;
  let lastVisibleCount = 0;
  let pendingLayoutSync = false;
  let pendingRestartAutoplay = false;

  const rootStyles = window.getComputedStyle(document.documentElement);
  const breakpointMd = Number.parseFloat(rootStyles.getPropertyValue('--breakpoint-md')) || 768;
  const breakpointLg = Number.parseFloat(rootStyles.getPropertyValue('--breakpoint-lg')) || 1440;
  const mediaMd = window.matchMedia(`(min-width: ${breakpointMd}px)`);
  const mediaLg = window.matchMedia(`(min-width: ${breakpointLg}px)`);

  const getVisibleCount = () => {
    if (mediaLg.matches) {
      return 3;
    }

    if (mediaMd.matches) {
      return 2;
    }

    return 1;
  };

  const getStepSize = () => (controlMode === 'arrows' ? getVisibleCount() : 1);

  const isSwipeEnabled = () => !mediaLg.matches;

  const getMaxIndex = () => Math.max(0, realSlides.length - getStepSize());

  const canGoPrev = () => selectedIndex > 0;

  const canGoNext = () => selectedIndex < getMaxIndex();

  const setButtonTooltip = (button, text) => {
    if (!button || !text) {
      return;
    }

    button.dataset.tooltip = text;
  };

  const getPrevTooltip = () => {
    if (!canGoPrev()) {
      return 'No previous testimonials';
    }

    if (isAnimating) {
      return 'Please wait';
    }

    return 'Show previous testimonials';
  };

  const getNextTooltip = () => {
    if (!canGoNext()) {
      return 'No more testimonials';
    }

    if (isAnimating) {
      return 'Please wait';
    }

    return 'Show next testimonials';
  };

  const normalizeIndex = (index) => {
    const total = realSlides.length;
    return ((index % total) + total) % total;
  };

  const stopAutoplay = () => {
    if (autoplayTimer !== null) {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    }
  };

  const clearSnapTimer = () => {
    if (snapTimer !== null) {
      window.clearTimeout(snapTimer);
      snapTimer = null;
    }
  };

  const setTrackPosition = (animate) => {
    const slide = allSlides[currentTrackIndex];

    if (!slide) {
      return;
    }

    // Ensure flex slide offsets are measured after layout changes.
    void track.offsetHeight;

    track.style.transition = animate ? `transform ${TRANSITION_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)` : 'none';
    track.style.transform = `translate3d(-${slide.offsetLeft}px, 0, 0)`;
  };

  const updateVisibleSlides = () => {
    const visibleCount = getVisibleCount();

    allSlides.forEach((slide, index) => {
      const isVisible = index >= currentTrackIndex && index < currentTrackIndex + visibleCount;
      slide.setAttribute('aria-hidden', String(!isVisible));
      if ('inert' in slide) {
        slide.inert = !isVisible;
      }
    });
  };

  const updateControls = ({ focusControl = false } = {}) => {
    if (controlMode === 'pips') {
      pipButtons.forEach((button, index) => {
        if (index === selectedIndex) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });

      if (focusControl) {
        pipButtons[selectedIndex]?.focus();
      }
    }

    if (controlMode === 'arrows') {
      if (prevButton) {
        const isPrevDisabled = isAnimating || !canGoPrev();
        prevButton.disabled = isPrevDisabled;
        prevButton.setAttribute('aria-disabled', String(isPrevDisabled));
        setButtonTooltip(prevButton, getPrevTooltip());
      }

      if (nextButton) {
        const isNextDisabled = isAnimating || !canGoNext();
        nextButton.disabled = isNextDisabled;
        nextButton.setAttribute('aria-disabled', String(isNextDisabled));
        setButtonTooltip(nextButton, getNextTooltip());
      }
    }
  };

  const syncUi = ({ animate = true, focusControl = false } = {}) => {
    setTrackPosition(animate);
    updateVisibleSlides();
    updateControls(focusControl);
  };

  const finalizeLoopPosition = () => {
    if (controlMode === 'arrows') {
      isAnimating = false;
      return;
    }

    let snapped = false;

    if (currentTrackIndex >= realSlides.length + CLONE_COUNT) {
      currentTrackIndex -= realSlides.length;
      snapped = true;
    } else if (currentTrackIndex < CLONE_COUNT) {
      currentTrackIndex += realSlides.length;
      snapped = true;
    }

    if (snapped) {
      setTrackPosition(false);
    }

    isAnimating = false;
  };

  const startAutoplay = () => {
    stopAutoplay();

    if (prefersReducedMotion.matches || document.hidden || isAnimating) {
      return;
    }

    if (controlMode === 'arrows' && !canGoNext()) {
      return;
    }

    autoplayTimer = window.setTimeout(() => {
      goToRelative(getStepSize());
    }, AUTOPLAY_DELAY);
  };

  const finishTransition = () => {
    clearSnapTimer();
    finalizeLoopPosition();
    updateVisibleSlides();
    updateControls();
    startAutoplay();

    if (pendingLayoutSync) {
      const restartAutoplay = pendingRestartAutoplay;
      pendingLayoutSync = false;
      pendingRestartAutoplay = false;

      window.requestAnimationFrame(() => {
        clearSnapTimer();
        syncLayout({ restartAutoplay });
      });
    }
  };

  const goToRelative = (delta, { focusControl = false } = {}) => {
    if (isAnimating) {
      return;
    }

    if (controlMode === 'arrows') {
      if (delta < 0 && !canGoPrev()) {
        return;
      }

      if (delta > 0 && !canGoNext()) {
        return;
      }
    }

    clearSnapTimer();
    stopAutoplay();

    if (controlMode === 'arrows') {
      selectedIndex += delta;
      currentTrackIndex = toTrackIndex(selectedIndex);
    } else {
      selectedIndex = normalizeIndex(selectedIndex + delta);
      currentTrackIndex += delta;
    }

    isAnimating = true;
    syncUi({ animate: true, focusControl });

    snapTimer = window.setTimeout(finishTransition, TRANSITION_MS + 60);
  };

  const goToIndex = (index, { focusControl = false } = {}) => {
    if (isAnimating || index === selectedIndex) {
      return;
    }

    clearSnapTimer();
    stopAutoplay();

    selectedIndex = normalizeIndex(index);
    currentTrackIndex = toTrackIndex(selectedIndex);
    isAnimating = true;
    syncUi({ animate: true, focusControl });

    snapTimer = window.setTimeout(finishTransition, TRANSITION_MS + 60);
  };

  const syncLayout = ({ restartAutoplay = false } = {}) => {
    if (controlMode === 'arrows') {
      selectedIndex = Math.min(selectedIndex, getMaxIndex());
    }

    currentTrackIndex = toTrackIndex(selectedIndex);
    lastVisibleCount = getVisibleCount();
    setTrackPosition(false);
    updateVisibleSlides();
    updateControls();

    if (restartAutoplay) {
      startAutoplay();
    }
  };

  const handleLayoutChange = ({ restartAutoplay = true } = {}) => {
    const visibleCount = getVisibleCount();
    const breakpointChanged = visibleCount !== lastVisibleCount;

    if (controlMode === 'arrows') {
      selectedIndex = Math.min(selectedIndex, getMaxIndex());
      currentTrackIndex = toTrackIndex(selectedIndex);
    }

    if (breakpointChanged) {
      updateControls();
    }

    if (isAnimating) {
      pendingLayoutSync = true;
      if (restartAutoplay) {
        pendingRestartAutoplay = true;
      }
      return;
    }

    clearSnapTimer();
    syncLayout({ restartAutoplay });
  };

  const handleResize = () => {
    handleLayoutChange({ restartAutoplay: true });
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      stopAutoplay();
      return;
    }

    startAutoplay();
  };

  const handleSwipe = () => {
    if (!isSwiping || isAnimating || !isSwipeEnabled()) {
      isSwiping = false;
      return;
    }

    const swipeDistance = touchStartX - touchEndX;

    if (Math.abs(swipeDistance) < SWIPE_MIN_DISTANCE) {
      isSwiping = false;
      return;
    }

    stopAutoplay();

    if (swipeDistance > 0) {
      goToRelative(getStepSize());
    } else {
      goToRelative(-getStepSize());
    }

    isSwiping = false;
  };

  const handleTouchStart = (event) => {
    if (!isSwipeEnabled()) {
      return;
    }

    isSwiping = true;
    touchStartX = event.changedTouches[0].screenX;
  };

  const handleTouchEnd = (event) => {
    if (!isSwiping) {
      return;
    }

    touchEndX = event.changedTouches[0].screenX;
    handleSwipe();
  };

  const handleMediaChange = () => {
    handleLayoutChange({ restartAutoplay: true });
  };

  const handleRefresh = () => {
    handleLayoutChange({ restartAutoplay: true });
  };

  const handlePrevClick = () => {
    goToRelative(-getStepSize());
  };

  const handleNextClick = () => {
    goToRelative(getStepSize());
  };

  if (controlMode === 'pips') {
    pipButtons.forEach((button, index) => {
      button.addEventListener('click', () => {
        goToIndex(index);
      });
    });
  }

  if (controlMode === 'arrows') {
    prevButton.addEventListener('click', handlePrevClick);
    nextButton.addEventListener('click', handleNextClick);
  }

  carousel.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      goToRelative(-getStepSize(), { focusControl: controlMode === 'pips' });
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      goToRelative(getStepSize(), { focusControl: controlMode === 'pips' });
    }

    if (event.key === 'Home') {
      event.preventDefault();
      goToIndex(0, { focusControl: controlMode === 'pips' });
    }

    if (event.key === 'End') {
      event.preventDefault();
      goToIndex(controlMode === 'arrows' ? getMaxIndex() : realSlides.length - 1, {
        focusControl: controlMode === 'pips',
      });
    }
  });

  carousel.addEventListener('testimonial-carousel:refresh', handleRefresh);
  window.addEventListener('resize', handleResize);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  mediaMd.addEventListener('change', handleMediaChange);
  mediaLg.addEventListener('change', handleMediaChange);

  let viewportResizeObserver = null;
  let layoutFrame = null;

  const scheduleLayoutChange = () => {
    if (layoutFrame !== null) {
      window.cancelAnimationFrame(layoutFrame);
    }

    layoutFrame = window.requestAnimationFrame(() => {
      layoutFrame = null;
      handleLayoutChange({ restartAutoplay: true });
    });
  };

  if (typeof ResizeObserver !== 'undefined') {
    viewportResizeObserver = new ResizeObserver(() => {
      scheduleLayoutChange();
    });
    viewportResizeObserver.observe(carousel);

    if (viewport) {
      viewportResizeObserver.observe(viewport);
    }
  }

  if (viewport) {
    viewport.addEventListener('touchstart', handleTouchStart, { passive: true });
    viewport.addEventListener('touchend', handleTouchEnd, { passive: true });
  }

  if (controlMode === 'arrows') {
    selectedIndex = Math.min(selectedIndex, getMaxIndex());
    currentTrackIndex = toTrackIndex(selectedIndex);
  }

  lastVisibleCount = getVisibleCount();

  const startCarousel = () => {
    syncUi({ animate: false });
    startAutoplay();
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(startCarousel);
  });

  return () => {
    stopAutoplay();
    clearSnapTimer();
    window.removeEventListener('resize', handleResize);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    mediaMd.removeEventListener('change', handleMediaChange);
    mediaLg.removeEventListener('change', handleMediaChange);
    carousel.removeEventListener('testimonial-carousel:refresh', handleRefresh);

    if (viewportResizeObserver) {
      viewportResizeObserver.disconnect();
    }

    if (layoutFrame !== null) {
      window.cancelAnimationFrame(layoutFrame);
    }

    if (viewport) {
      viewport.removeEventListener('touchstart', handleTouchStart);
      viewport.removeEventListener('touchend', handleTouchEnd);
    }
    pipButtons.forEach((button) => {
      button.removeAttribute('aria-current');
    });
    if (controlMode === 'arrows') {
      prevButton?.removeEventListener('click', handlePrevClick);
      nextButton?.removeEventListener('click', handleNextClick);
    }
    [prevButton, nextButton].forEach((button) => {
      if (!button) {
        return;
      }

      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.removeAttribute('data-tooltip');
    });
    allSlides.forEach((slide) => {
      slide.removeAttribute('aria-hidden');
      if ('inert' in slide) {
        slide.inert = false;
      }
    });
    track.style.transition = '';
    track.style.transform = '';
    track.querySelectorAll('[data-clone="true"]').forEach((clone) => clone.remove());
    delete carousel.dataset.testimonialCarouselInitialized;
  };
}

/**
 * @param {ParentNode | Element | Document} [root=document]
 */
export function initTestimonialCarousel(root = document) {
  const scope = typeof root.querySelectorAll === 'function' ? root : document;
  const carousels = Array.from(scope.querySelectorAll('[data-testimonial-carousel]'));

  if (!carousels.length) {
    return () => {};
  }

  const cleanups = carousels.map((carousel) => initSingleTestimonialCarousel(carousel));

  return () => {
    cleanups.forEach((cleanup) => cleanup());
  };
}

/**
 * Re-sync carousel layout after external viewport or container size changes.
 *
 * @param {ParentNode | Element | Document} [root=document]
 */
export function refreshTestimonialCarousel(root = document) {
  const scope = typeof root.querySelectorAll === 'function' ? root : document;
  const carousels = Array.from(scope.querySelectorAll('[data-testimonial-carousel]'));

  carousels.forEach((carousel) => {
    carousel.dispatchEvent(new CustomEvent('testimonial-carousel:refresh'));
  });
}
